import React, { useState, useEffect } from "react";
import {
  Card,
  Container,
  Form,
  Modal,
  Table,
  InputGroup,
  FormControl,
  Button,
} from "react-bootstrap";
import Select from "react-select";
import { FiEdit, FiUpload, FiDownload, FiPlus } from "react-icons/fi";
import { MdAddchart } from "react-icons/md";
import { toast } from "react-toastify";
import Pagination from "../Constant/Pagination";
import { Spinner } from "react-bootstrap";
import { MultiActionDropdown } from "../../components";
import { FiMapPin } from "react-icons/fi";
import { FaMapLocationDot } from "react-icons/fa6";

import {
  getAllLocations,
  createLocation,
  updateLocation,
  uploadLocationExcel,
} from "../../services/SettingServices";
import GlobalSearchableDropdown from "../Constant/SearchableDropdown";
import DebounceSmallSearchInput from "../Constant/DebounceSmallSearchInput";

const LocationSettings = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null); // null = all

  const [formData, setFormData] = useState({ name: "", is_active: true });
  const [editData, setEditData] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getAllLocations({
        page,
        page_size: pageSize,
        search,
        ...(statusFilter !== null && { is_active: statusFilter }),
      });
      setData(response.data.results || []);
      setCount(response.data.count || 0);
    } catch (err) {
      toast.error("Failed to fetch locations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(fetchData, 300);
    return () => clearTimeout(timeout);
  }, [search, page, statusFilter]);
  const statusOptions = [
    { value: true, label: "Active" },
    { value: false, label: "Inactive" },
  ];

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadLocationExcel(file);
      toast.success(res.data.message || "Upload successful");
      await fetchData();
      setShowUploadModal(false);
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleExport = async () => {
    try {
      const res = await getAllLocations({ export: true });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "locations_export.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export successful!");
    } catch (err) {
      toast.error("Failed to export locations");
    }
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error("Location name is required.");
      return;
    }

    try {
      await createLocation(formData);
      fetchData();
      setFormData({ name: "", is_active: true });
      setShowModal(false);
      toast.success("Location added successfully!");
    } catch (err) {
      const msg =
        err?.response?.data?.name?.[0] ||
        err?.response?.data?.detail ||
        "Failed to create location";
      toast.error(msg);
    }
  };

  const handleUpdate = async () => {
    if (!editData?.name?.trim()) {
      toast.error("Location name is required.");
      return;
    }

    try {
      await updateLocation(editData.id, editData);
      fetchData();
      setShowEditModal(false);
      toast.success("Location updated successfully!");
    } catch (err) {
      const msg =
        err?.response?.data?.name?.[0] ||
        err?.response?.data?.detail ||
        "Failed to update location";
      toast.error(msg);
    }
  };

  const StatusBadge = ({ isActive }) => (
    <span
      className={`badge ${
        isActive ? "text-bg-success-soft" : "text-bg-danger-soft"
      }`}
    >
      {isActive ? "Active" : "Inactive"}
    </span>
  );

  return (
    <>
      <div className="card p-2 mb-2">
        <div className="d-flex flex-column flex-md-row  justify-content-between align-items-center gap-1">
          <div className="flex-grow-1 flex-grow-md-0">
            <h5 className="d-flex align-items-center gap-1 mb-0">
              <FiMapPin size={16} className="text-primary"/>
              Locations
            </h5>
          </div>
          <div className="d-flex gap-2 align-items-center flex-wrap">
            <DebounceSmallSearchInput
              placeholder="Search locations"
              handleSearchChange={(value) => setSearch(value)}
            />
            {/* Status Dropdown */}
            <GlobalSearchableDropdown
              options={statusOptions}
              selected={
                statusFilter !== null
                  ? statusOptions.find(
                      (option) => option.value === statusFilter
                    )
                  : null
              }
              onSelect={(selectedOption) => {
                setStatusFilter(
                  selectedOption ? selectedOption.value : null
                );
              }}
              placeholder="Status"
              isClearable={true}
            />

            <MultiActionDropdown
              variant="outline-primary"
              btnText="Add"
              btnIcon={<MdAddchart />}
              btnOnClick={() => setShowModal(true)}
              menuOptions={[
                {
                  label: "Add",
                  icon: <FiPlus />,
                  onClick: () => setShowModal(true),
                },
                {
                  label: "Import",
                  icon: <FiUpload />,
                  onClick: () => setShowUploadModal(true),
                },
                {
                  label: "Export",
                  icon: <FiDownload />,
                  onClick: handleExport,
                },
              ]}
            />
          </div>
        </div>
      </div>

      <Container fluid className="p-0">
        <Card>
          <Card.Body className="p-0">
            {loading ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ minHeight: "110px" }}
              >
                <Spinner animation="border" size={35} color="#255d5c" />
              </div>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="table fs-6 mb-0 align-middle">
                    <thead className="table-light custom-thead">
                      <tr>
                        <th>Name</th>
                        <th>Active</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.length === 0 ? (
                        <tr>
                          <td colSpan={3} className="text-center py-3">
                            No data found
                          </td>
                        </tr>
                      ) : (
                        data.map((loc, i) => (
                          <tr key={i}>
                            <td>{loc.name}</td>
                            <td>
                              <StatusBadge isActive={loc.is_active} />
                            </td>
                            <td>
                              <FiEdit
                                size={14}
                                className="text-primary"
                                style={{ cursor: "pointer" }}
                                title="Edit"
                                onClick={() => {
                                  setEditData(loc);
                                  setShowEditModal(true);
                                }}
                              />
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                  
                <div className="d-flex justify-content-end mt-3">
                  <Pagination
                    currentPage={page}
                    recordsPage={pageSize}
                    dataLength={count}
                    handlePagination={(p) => setPage(p.selected + 1)}
                    showPageInfo={false}
                  />
                </div>
              </>
            )}
          </Card.Body>
        </Card>

        {/* Add Modal */}
        <Modal show={showModal} onHide={() => setShowModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Add Location</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group>
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="Enter location name"
                />
              </Form.Group>
              <Form.Group className="mt-3">
                <Form.Check
                  label="Active"
                  checked={formData.is_active}
                  type="checkbox"
                  onChange={(e) =>
                    setFormData({ ...formData, is_active: e.target.checked })
                  }
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Close
            </Button>
            <Button variant="primary" onClick={handleCreate}>
              Save
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Edit Modal */}
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Edit Location</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group>
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  value={editData?.name || ""}
                  onChange={(e) =>
                    setEditData({ ...editData, name: e.target.value })
                  }
                />
              </Form.Group>
              <Form.Group className="mt-3">
                <Form.Check
                  label="Active"
                  checked={editData?.is_active || false}
                  type="checkbox"
                  onChange={(e) =>
                    setEditData({ ...editData, is_active: e.target.checked })
                  }
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleUpdate}>
              Update
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Upload Modal */}
        <Modal show={showUploadModal} onHide={() => setShowUploadModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Upload Locations (Excel)</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group>
              <Form.Label>Select Excel File (.xlsx)</Form.Label>
              <Form.Control
                type="file"
                accept=".xls,.xlsx"
                onChange={handleUpload}
                disabled={uploading}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowUploadModal(false)}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </>
  );
};

export default LocationSettings;
